import { CallState, type MapState } from "@b2bportal/core-types";
import {
  type AvailabilityResponse,
  GroundSelectionEnum,
  type GroundSupplier,
} from "@b2bportal/ground-api";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import type {
  CarSortOptionEnum,
  FilterState,
  SearchDetails,
  DropoffType,
} from "../../../types";
import type { CarLocationSuggestion } from "../components";
import {
  initialFiltersState,
  initialCarAvailabilityState,
  initialCarsAvailabilitySearchState,
} from "./initialState";
import { fetchCars, initializeSearchState } from "./thunks";
import type { CarsAvailabilitySearchState } from "./types";
import { dateTimeToDateAndTime } from "./utils";
import type {
  CarTypeFilter,
  Specification,
  MinimumBagCount,
  PassengerFilter,
  PriceRange,
} from "~core-cars/types";
import { toMapCoordinates } from "~core-cars/utilities";

export const carsAvailabilitySlice = createSlice({
  name: "carsAvailability",
  initialState: initialCarAvailabilityState,
  reducers: {
    setNewPickupLocation: (
      state,
      action: PayloadAction<CarLocationSuggestion>
    ) => {
      state.searchFormValues.pickupLocation = action.payload;
    },
    setNewPickupDate: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.pickupDate = action.payload;
    },
    setNewPickupTime: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.pickupTime = action.payload;
      state.searchFormValues.pickupDateTime = action.payload;
    },
    setNewDropoffType: (state, action: PayloadAction<DropoffType>) => {
      state.searchFormValues.dropoffType = action.payload;
    },
    setNewDropoffLocation: (
      state,
      action: PayloadAction<CarLocationSuggestion>
    ) => {
      state.searchFormValues.dropoffLocation = action.payload;
    },
    setNewDropoffDate: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.dropoffDate = action.payload;
    },
    setNewDropoffTime: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.dropoffTime = action.payload;

      state.searchFormValues.dropoffDateTime = action.payload;
    },
    setNewDriverAge: (state, action: PayloadAction<number | undefined>) => {
      state.searchFormValues.driverAge = action.payload;
    },
    setRequestedSearch: (state, action: PayloadAction<SearchDetails>) => {
      const { date: pickupDate, time: pickupTime } = dateTimeToDateAndTime(
        action.payload.pickupTime
      );
      const { date: dropoffDate, time: dropoffTime } = dateTimeToDateAndTime(
        action.payload.dropoffTime
      );

      state.searchFormValues = {
        ...action.payload,
        pickupDate,
        pickupTime,
        dropoffDate,
        dropoffTime,
        pickupDateTime: pickupTime,
        dropoffDateTime: dropoffTime,
      };
      state.requestedSearch = action.payload;
    },
    resetFilters(state) {
      state.filters = initialFiltersState;
    },
    setAllFilters(state, action: PayloadAction<FilterState>) {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    setPriceFilter(state, action: PayloadAction<PriceRange>) {
      state.filters.priceRange = action.payload;
    },

    setCarTypeFilter(state, action: PayloadAction<Array<CarTypeFilter>>) {
      state.filters.carType = action.payload;
    },
    setSupplierFilter(state, action: PayloadAction<Array<GroundSupplier>>) {
      state.filters.supplier = action.payload;
    },
    setSpecificationFilter(state, action: PayloadAction<Specification>) {
      state.filters.specification = action.payload;
    },
    setCancellationFilter(state, action: PayloadAction<boolean>) {
      state.filters.freeCancellation = action.payload;
    },
    setMinimumBagsFilter(state, action: PayloadAction<MinimumBagCount>) {
      state.filters.minimumBags = action.payload;
    },
    setPassengerFilter(state, action: PayloadAction<Array<PassengerFilter>>) {
      state.filters.passenger = action.payload;
    },
    setSort: (state, action: PayloadAction<CarSortOptionEnum>) => {
      state.sort = action.payload;
    },
    setMapData: (state, action: PayloadAction<MapState>) => {
      state.map = action.payload;
    },
    resetSearchResults: (state) => {
      state.searchResult = {
        state: CallState.NotCalled,
        data: undefined,
      };
      state.search = undefined;
      state.requestedSearch = undefined;
    },
    resetPriceFilter(state) {
      state.filters.priceRange = initialFiltersState.priceRange;
    },
    resetCarTypeFilter(state) {
      state.filters.carType = initialFiltersState.carType;
    },
    resetSupplierFilter(state) {
      state.filters.supplier = initialFiltersState.supplier;
    },
    resetSpecificationFilter(state) {
      state.filters.specification = initialFiltersState.specification;
    },
    resetCancellationFilter(state) {
      state.filters.freeCancellation = initialFiltersState.freeCancellation;
    },
    resetMinimumBagsFilter(state) {
      state.filters.minimumBags = initialFiltersState.minimumBags;
    },
    resetPassengerFilter(state) {
      state.filters.passenger = initialFiltersState.passenger;
    },
    // actions from packages
    setCarsAvailabilityFromPackagesLoading(state) {
      state.searchResult = {
        state: CallState.InProcess,
        data: undefined,
      };
    },
    setCarsAvailabilityFromPackages: (
      state,
      action: PayloadAction<AvailabilityResponse>
    ) => {
      if (
        state.map.centroid == null ||
        state.search?.searchType !== GroundSelectionEnum.Location
      ) {
        const searchCentroid = toMapCoordinates(
          action.payload.searchCoordinates
        );
        const firstResult = action.payload.vehicles[0];
        state.map.centroid =
          firstResult != null
            ? toMapCoordinates(firstResult.pickUp.coordinates)
            : searchCentroid;
        state.map.zoom = initialCarsAvailabilitySearchState.map.zoom;
      }

      state.search = state.requestedSearch;
      state.searchResult = {
        state: CallState.Success,
        data: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeSearchState.pending, (state) => {
        state.initialized = CallState.InProcess;
        state.searchResult = {
          state: CallState.InProcess,
          data: undefined,
        };
      })
      .addCase(
        initializeSearchState.fulfilled,
        (state, action: PayloadAction<CarsAvailabilitySearchState>) => {
          state.initialized = CallState.Success;
          state.requestedSearch = action.payload.requestedSearch;
          state.searchFormValues = action.payload.searchFormValues;
          state.map = action.payload.map ?? state.map;
          state.filters = action.payload.filters ?? state.filters;
          state.sort = action.payload.sort ?? state.sort;
        }
      )
      .addCase(initializeSearchState.rejected, (state, action) => {
        state.initialized = CallState.Failed;
        state.searchResult = {
          state: CallState.Failed,
          data: undefined,
          error: action.payload?.error ?? JSON.stringify(action.error),
        };
      })
      .addCase(fetchCars.pending, (state) => {
        state.searchResult = {
          state: CallState.InProcess,
          data: undefined,
        };
      })
      .addCase(
        fetchCars.fulfilled,
        (state, action: PayloadAction<AvailabilityResponse>) => {
          if (
            state.map.centroid == null ||
            state.search?.searchType !== GroundSelectionEnum.Location
          ) {
            const searchCentroid = toMapCoordinates(
              action.payload.searchCoordinates
            );
            const firstResult = action.payload.vehicles[0];

            state.map.centroid =
              firstResult != null
                ? toMapCoordinates(firstResult.pickUp.coordinates)
                : searchCentroid;
            state.map.zoom = initialCarsAvailabilitySearchState.map.zoom;
          }

          state.search = state.requestedSearch;
          state.searchResult = {
            state: CallState.Success,
            data: action.payload,
          };
        }
      )
      .addCase(fetchCars.rejected, (state, action) => {
        state.search = state.requestedSearch;
        state.searchResult = {
          state: CallState.Failed,
          data: undefined,
          error: action.payload?.error ?? JSON.stringify(action.error),
        };
      });
  },
});

export type CarsAvailabilityActionTypes = ReturnType<
  (typeof CarsAvailabilityActions)[keyof typeof CarsAvailabilityActions]
>;

export const CarsAvailabilityActions = carsAvailabilitySlice.actions;

export default carsAvailabilitySlice.reducer;
